/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import React from 'react';
import StudioContainer from './StudioContainer';

// Services
import GraphsService from './services/graphs/GraphsService';
import TenantProvidersService from './services/tenants/TenantProvidersService';
import TenantsService from './services/tenants/TenantsService';
// Spec Model
import { POST_RUN_EVAL_LENS_EVALUATION } from './constants/ServiceConstants';
import TenantsModel from './models/TenantsModel';
import GraphsModel from './models/graphs/GraphsModel';
import DeleteContentService from './services/contentUpload/DeleteContentService';
import GetUploadedContentService from './services/contentUpload/GetUploadedContentService';
import UploadContentService from './services/contentUpload/UploadContentService';
import GetGraphAccessService from './services/graphShare/GetGraphAccessService';
import RevokeGraphAccessService from './services/graphShare/RevokeGraphAccessService';
import ShareGraphService from './services/graphShare/ShareGraphService';
import UpdateGraphAccessService from './services/graphShare/UpdateGraphAccessService';
import CopyGraphService from './services/graphs/CopyGraphService';
import CreateGraphService from './services/graphs/CreateGraphService';
import CreateVersionService from './services/graphs/CreateVersionService';
import PromoteGraphService from './services/graphs/PromoteGraphService';
import DeleteGraphService from './services/graphs/DeleteGraphService';
import GetGraphCodeService from './services/graphs/GetGraphCodeService';
import GetGraphForHistoryService from './services/graphs/GetGraphForHistoryService';
import GetGraphHistoryService from './services/graphs/GetGraphHistoryService';
import GetGraphHistoryVersionService from './services/graphs/GetGraphHistoryVersionService';
import GetGraphService from './services/graphs/GetGraphService';
import GetGraphToolsService from './services/graphs/GetGraphToolsService';
import GetGraphsService from './services/graphs/GetGraphsService';
import GetTargetTenantsService from './services/graphs/GetTargetTenantsService';
import GraphDeleteParamsService from './services/graphs/GraphDeleteParamsService';
import GraphGetParamsService from './services/graphs/GraphGetParamsService';
import GraphSaveParamsService from './services/graphs/GraphSaveParamsService';
import PublishGraphService from './services/graphs/PublishGraphService';
import RunEvaluationCriteriaService from './services/graphs/RunEvaluationCriteriaService';
import SaveEvaluationRubricTextService from './services/graphs/SaveEvaluationRubricTextService';
import SaveEvaluationSchedulerService from './services/graphs/SaveEvaluationSchedulerService';
import SetGraphDefaultService from './services/graphs/SetGraphDefaultService';
import UpdateGraphService from './services/graphs/UpdateGraphService';
import UpdateGraphTitleService from './services/graphs/UpdateGraphTitleService';
import ListGraphEvaluationsService from './services/graphs/ListGraphEvaluationsService';
import CreateEvaluationService from './services/graphs/CreateEvaluationService';
import DeleteEvaluationService from './services/graphs/DeleteEvaluationService';
import GetExecutionLogsService from './services/logs/GetExecutionLogsService';
import GetLLMLogsService from './services/logs/GetLLMLogsService';
import GetModelPropertiesService from './services/models/GetModelPropertiesService';
import GetCdnTokenService from './services/tokens/GetCdnTokenService';
import GetPromptVersionsService from './services/prompt/GetPromptVersionsService';
import GetPromptVersionService from './services/prompt/GetPromptVersionService';
import GetPromptCatalogueService from './services/prompt/GetPromptCatalogueService';
import GetPromptNameAvailabilityService from './services/prompt/GetPromptNameAvailabilityService';
import CreatePromptService from './services/prompt/CreatePromptService';
import UpdatePromptService from './services/prompt/UpdatePromptService';
// Spec Model
import GraphTesterModel from './models/GraphTesterModel';
import ModelPropertiesMappingModel from './models/ModelPropertiesMappingModel';
import ContentUploadModel from './models/contentUpload/ContentUploadModel';
import FeatureConfigModel from './models/featureConfig/FeatureConfigModel';
import AgenticToolsModel from './models/graphs/AgenticToolsModel';
import GraphCodeModel from './models/graphs/GraphCodeModel';
import GraphHistoryCompareModel from './models/graphs/GraphHistoryCompareModel';
import GraphHistoryModel from './models/graphs/GraphHistoryModel';
import GraphHistoryVersionModel from './models/graphs/GraphHistoryVersionModel';
import GraphListModel from './models/graphs/GraphListModel';
import GraphModel from './models/graphs/GraphModel';
import GraphParamsModel from './models/graphs/GraphParamsModel';
import GraphAccessModel from './models/shareGraph/GraphAccessModel';
import ShareGraphModel from './models/shareGraph/ShareGraphModel';
import GetFeatureConfigService from './services/featureConfig/GetFeatureConfigService';
import { BERLIN_PIPELINE_PHASE } from './vibeflow/constants/berlinBuildConstants';
import VibeFlowModel from './vibeflow/models/VibeFlowModel';
import StudioPromptModel from './models/prompt/StudioPromptModel';

export default {
  path: [
    '/berlin',
    '/berlin/:tenantname/studio',
    '/berlin/:tenantname/studio/graph/:graphId/:version',
    '/berlin/testbed',
    '/berlin/testbed/:tenantName/launch',
    '/berlin/guided-study-testbed',
    '/berlin/guided-study-testbed/:tenantName/launch',
    '/berlin/office-hour-testbed',
    '/berlin/office-hour-testbed/:tenantName/launch',
    '/berlin/office-hour-aist-testbed',
    '/berlin/office-hour-aist-testbed/:tenantName/launch',
    '/berlin/:tenantName/quizaet',
    '/berlin/*'
  ],
  exact: true,
  services: [
    new TenantsService(),
    new TenantProvidersService(),
    new GraphsService(),
    new DeleteGraphService(),
    new UpdateGraphTitleService(),
    new ShareGraphService(),
    new GetGraphAccessService(),
    new UpdateGraphAccessService(),
    new RevokeGraphAccessService(),
    new GraphGetParamsService(),
    new GraphSaveParamsService(),
    new GraphDeleteParamsService(),
    new GetGraphService(),
    new CreateGraphService(),
    new UpdateGraphService(),
    new SaveEvaluationRubricTextService(),
    new SaveEvaluationSchedulerService(),
    new ListGraphEvaluationsService(),
    new CreateEvaluationService(),
    new DeleteEvaluationService(),
    new RunEvaluationCriteriaService(POST_RUN_EVAL_LENS_EVALUATION, 'postRunEvalLensEvaluationStatus'),
    new CreateVersionService(),
    new PromoteGraphService(),
    new PublishGraphService(),
    new CopyGraphService(),
    new SetGraphDefaultService(),
    new GetExecutionLogsService(),
    new GetLLMLogsService(),
    new GetGraphsService(),
    new GetFeatureConfigService(),
    new GetGraphCodeService(),
    new GetGraphToolsService(),
    // GET_SYS2SYS_TOKEN is handled by aiadmin's GetSys2sysTokenService. Both listened on the same event
    // name, so registering it here too made every token request hit /token/system twice.
    new GetCdnTokenService(),
    new GetGraphForHistoryService(),
    new GetGraphHistoryService(),
    new GetGraphHistoryVersionService(),
    new GetTargetTenantsService(),
    new GetModelPropertiesService(),
    new UploadContentService(),
    new GetUploadedContentService(),
    new DeleteContentService(),
    new GetPromptVersionsService(),
    new GetPromptVersionService(),
    new GetPromptCatalogueService(),
    new GetPromptNameAvailabilityService(),
    new CreatePromptService(),
    new UpdatePromptService()
  ],
  stores: [
    {
      name: 'tenantState',
      spec: TenantsModel,
      saveLocally: false,
      initialState: {
        tenantList: [],
        providerList: [],
        selectedTenant: {},
        targetTenantList: []
      }
    },
    {
      name: 'graphs',
      spec: GraphsModel,
      saveLocally: false,
      initialState: {}
    },
    {
      name: 'graphModel',
      spec: GraphModel,
      saveLocally: false,
      initialState: {
        graphData: null,
        settings: {
          skipHistory: false,
          skipContentFiltering: false,
          showAdvancedSettings: false,
          currentGraphId: null,
          currentTenantId: null,
          currentGraphStatus: '',
          evaluations: [],
          otherParams: {}
        },
        error: null
      }
    },
    {
      name: 'agenticToolsModel',
      spec: AgenticToolsModel,
      saveLocally: false,
      initialState: {
        toolsData: null,
        error: null
      }
    },
    {
      name: 'shareGraphModel',
      spec: ShareGraphModel,
      saveLocally: false,
      initialState: {
        sharedWith: [],
        notFound: [],
        error: null
      }
    },
    {
      name: 'graphAccessModel',
      spec: GraphAccessModel,
      saveLocally: false,
      initialState: {
        accessList: [],
        owner: null,
        error: null,
        updateSuccess: false
      }
    },
    {
      name: 'graphParamsModel',
      spec: GraphParamsModel,
      saveLocally: false,
      initialState: {
        loading: false,
        hasSavedConfig: false,
        params: null,
        error: null
      }
    },
    {
      name: 'graphTesterModel',
      spec: GraphTesterModel,
      saveLocally: false,
      initialState: {
        executionLogs: null,
        simplifiedExecutionLogs: null,
        executionLogsLoading: false,
        executionLogsError: null,
        llmLogs: null,
        llmLogsLoading: false,
        llmLogsError: null,
        llmStats: null
      }
    },
    {
      name: 'graphListModel',
      spec: GraphListModel,
      saveLocally: false,
      initialState: {
        graphs: [],
        error: null
      }
    },
    {
      name: 'graphCodeModel',
      spec: GraphCodeModel,
      saveLocally: false,
      initialState: {
        code: null,
        error: null
      }
    },
    {
      name: 'graphHistoryCompareModel',
      spec: GraphHistoryCompareModel,
      saveLocally: false,
      initialState: {
        graphData: null
      }
    },
    {
      name: 'graphHistoryModel',
      spec: GraphHistoryModel,
      saveLocally: false,
      initialState: {
        historyData: null
      }
    },
    {
      name: 'graphHistoryVersionModel',
      spec: GraphHistoryVersionModel,
      saveLocally: false,
      initialState: {
        graphData: null
      }
    },
    {
      name: 'modelPropertiesMappingModel',
      spec: ModelPropertiesMappingModel,
      saveLocally: false,
      initialState: {
        data: [],
        error: null
      }
    },
    {
      name: 'contentUploadModel',
      spec: ContentUploadModel,
      saveLocally: false,
      initialState: {
        contents: [],
        isUploading: false,
        isLoading: false,
        uploadError: null,
        loadError: null,
        lastUploadedFile: null
      }
    },
    {
      name: 'featureConfigModel',
      spec: FeatureConfigModel,
      saveLocally: false,
      initialState: {
        config: null,
        error: null
      }
    },
    {
      name: 'vibeFlowModel',
      spec: VibeFlowModel,
      saveLocally: false,
      initialState: {
        builderPanelOpen: false,
        canvasMode: false,
        agentBuildPrompt: '',
        berlinDemoExchangeCount: 0,
        berlinPipelineRunning: false,
        berlinPipelinePhase: BERLIN_PIPELINE_PHASE.IDLE,
        berlinStructuredSteps: [],
        canvasSnapshot: null,
        canvasHandoffApplied: false
      }
    },
    {
      name: 'studioPromptModel',
      spec: StudioPromptModel,
      saveLocally: false,
      initialState: {
        activeResolved: null,
        versions: [],
        catalogue: [],
        lastError: null
      }
    }
  ],

  render: (props) => (
    <StudioContainer {...props} /> // eslint-disable-line react/prop-types
  )
};
